import React, { useEffect, useState } from "react";
import { listLeaderboard } from "@/services/leaderboard.service";
import { useAuth } from "@/context/AuthContext";
import { fmtMoney } from "@/lib/format";
import { Trophy, User as UserIcon } from "lucide-react";

const rankStyle = (rank) => {
  if (rank === 1) return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300";
  if (rank === 2) return "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200";
  if (rank === 3) return "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300";
  return "bg-secondary text-muted-foreground";
};

const Leaderboard = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { (async () => { try { setRows(await listLeaderboard()); } finally { setLoading(false); } })(); }, []);

  return (
    <div className="space-y-6" data-testid="leaderboard-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Leaderboard</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">Top earners</h1>
        <p className="mt-2 text-sm text-muted-foreground">Ranked by total earnings settled on PAYD. Get paid on more milestones to climb.</p>
      </div>

      {loading ? (
        <div className="text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <Trophy className="w-8 h-8 mx-auto text-muted-foreground/60" />
          <div className="mt-3 font-medium">No earnings yet.</div>
          <p className="mt-1 text-sm text-muted-foreground max-w-sm mx-auto">Once payments start settling, the top earners show up here.</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl divide-y divide-border">
          {rows.map((r) => {
            const isMe = r.user_id === user?.user_id;
            return (
              <div key={r.user_id} className={`flex items-center gap-4 px-4 sm:px-6 py-4 ${isMe ? "bg-accent/40" : ""}`} data-testid={`leaderboard-row-${r.rank}`}>
                <div className={`w-9 h-9 shrink-0 rounded-full grid place-items-center text-sm font-bold ${rankStyle(r.rank)}`}>
                  {r.rank <= 3 ? <Trophy className="w-4 h-4" /> : r.rank}
                </div>
                <div className="w-10 h-10 shrink-0 rounded-full bg-secondary border border-border overflow-hidden grid place-items-center">
                  {r.picture ? <img src={r.picture} alt="" className="w-full h-full object-cover" /> : <UserIcon className="w-5 h-5 text-muted-foreground" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{r.name}{isMe && <span className="ml-2 text-[10px] uppercase tracking-widest text-primary font-semibold">You</span>}</div>
                  <div className="text-xs text-muted-foreground">{r.deals} {r.deals === 1 ? "payment" : "payments"} · {r.karma} karma</div>
                </div>
                <div className="text-right font-display font-bold text-lg">{fmtMoney(r.total)}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Leaderboard;
